import { useState, type ReactNode } from 'react';
import type { ContactConversion, ConversionItem } from '@/domain';
import { useContactConversion, useConvertContacts } from '@/application/usecases/useClients';
import { strings } from '@/shared/strings';
import { AddressBlock } from '@/ui/data/AddressBlock';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { useConfirmStore } from '@/ui/primitives/confirmStore';
import { NoticeSheet } from '@/ui/primitives/NoticeSheet';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { readFailure } from './clientRefusal';
import styles from './ContactConversionSheet.module.css';

/** One of a doctor's two fields: what it was, and what it becomes or why it is left. */
function Change({
  label,
  before,
  becomes,
  reason,
  certain,
}: {
  label: string;
  /** The old text, as it was typed. */
  before: string;
  /** What it is converted to, or null when it is left for review. */
  becomes: ReactNode | null;
  /** Why it is left, in the server's words. */
  reason: string | null;
  /** What of it the conversion is sure of all the same, kept beside the old text. */
  certain?: ReactNode | null;
}) {
  return (
    <div className={styles.change}>
      <span className={styles.field}>{label}</span>
      <p className={styles.before} dir="auto">
        {before}
      </p>
      {becomes !== null ? (
        <div className={styles.after}>
          <span className={styles.word}>{strings.clients.convertBecomes}</span>
          <div className={styles.value}>{becomes}</div>
        </div>
      ) : (
        <>
          <p className={styles.review}>
            {strings.clients.convertNeedsReview(reason ?? '')}
          </p>
          {certain != null && (
            <div className={styles.after}>
              <span className={styles.word}>{strings.clients.convertCertain}</span>
              <div className={styles.value}>{certain}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Item({ item }: { item: ConversionItem }) {
  const { phone, address } = item;
  return (
    <li className={styles.item}>
      <div className={styles.who}>
        <b dir="auto">{item.name}</b>
        <span className={styles.code}>{item.code}</span>
        {item.clinic !== null && (
          <span className={styles.clinic} dir="auto">
            {item.clinic}
          </span>
        )}
      </div>
      {phone !== null && (
        <Change
          label={strings.clients.convertPhone}
          before={phone.before}
          becomes={phone.outcome === 'converted' ? (phone.afterDisplay ?? phone.after) : null}
          reason={phone.reason}
        />
      )}
      {address !== null && (
        <Change
          label={strings.clients.convertAddress}
          before={address.before}
          becomes={
            address.outcome === 'converted' && address.after !== null ? (
              <AddressBlock lines={address.after.lines} />
            ) : null
          }
          reason={address.reason}
          certain={address.after === null ? null : <AddressBlock lines={address.after.lines} />}
        />
      )}
    </li>
  );
}

/**
 * Converting the telephone numbers and addresses that were typed as free
 * text. The request of 6 October 2026 (fifth), section 2, "Existing data":
 * list the migration and its results before running it, convert only what is
 * certain, flag the rest for a person, and keep the original text.
 *
 * Opened from the notice on the Doctors list, by whoever holds `admin`. It is
 * the list the server makes of what WOULD happen, and reading it changes
 * nothing: the five counts first, then every doctor with something waiting,
 * each with the old text as it was typed and under it what it becomes, or why
 * it is left for review and what of it is certain all the same.
 *
 * Convert is one question, which says the counts again, and it is asked of
 * everybody whatever their role: it changes every doctor on the list at once.
 * The server then does exactly what the list said. A refusal (somebody saved a
 * doctor while it ran, so nothing was converted) is said in the server's
 * words and the list stays, to press Convert again. Done, a notice says what
 * was converted and what now waits for a person, and its OK goes back to the
 * list of doctors, which by then counts the ones that need review.
 */
export function ContactConversionSheet({ onClose }: { onClose: () => void }) {
  const preview = useContactConversion(true);
  const convert = useConvertContacts();
  const [done, setDone] = useState<ContactConversion | null>(null);

  if (done !== null) {
    return (
      <NoticeSheet
        title={strings.clients.convertedTitle}
        message={strings.clients.convertedMessage(done.counts)}
        okLabel={strings.clients.ok}
        onClose={onClose}
      />
    );
  }

  const { data } = preview;
  const waiting = data !== undefined && data.items.length > 0;

  function run(counts: ContactConversion['counts']) {
    useConfirmStore.getState().ask({
      title: strings.clients.convertTitle,
      statement: strings.clients.confirmConvert(counts),
      confirmLabel: strings.clients.convert,
      cancelLabel: strings.confirm.cancel,
      run: () => convert.mutateAsync().then(setDone),
    });
  }

  return (
    <BottomSheet
      tall
      title={strings.clients.convertTitle}
      lead={strings.clients.convertLead}
      closeLabel={strings.sheet.close}
      onClose={onClose}
      footer={
        <div className={sheetStyles.actions}>
          <Button onClick={onClose} disabled={convert.isPending}>
            {strings.clients.close}
          </Button>
          <Button
            variant="primary"
            className={sheetStyles.primary}
            disabled={!waiting}
            loading={convert.isPending}
            onClick={() => {
              if (data !== undefined) run(data.counts);
            }}
          >
            {strings.clients.convert}
          </Button>
        </div>
      }
    >
      {preview.isPending && <SkeletonRows rows={4} label={strings.clients.convertLoading} />}

      {preview.isError && (
        <p className={styles.error} role="alert">
          {readFailure(preview.error)}
          <Button onClick={() => void preview.refetch()}>{strings.errors.retry}</Button>
        </p>
      )}

      {data !== undefined && !waiting && (
        <div className={styles.nothing}>
          <h4>{strings.clients.convertNothing}</h4>
          <p>{strings.clients.convertNothingDetail}</p>
        </div>
      )}

      {data !== undefined && waiting && (
        <>
          <dl className={styles.counts} aria-label={strings.clients.convertCounts}>
            {(
              [
                [strings.clients.convertDoctors, data.counts.doctors],
                [strings.clients.convertPhonesConverted, data.counts.phonesConverted],
                [strings.clients.convertPhonesForReview, data.counts.phonesForReview],
                [strings.clients.convertAddressesConverted, data.counts.addressesConverted],
                [strings.clients.convertAddressesForReview, data.counts.addressesForReview],
              ] as const
            ).map(([label, count]) => (
              <div key={label} className={styles.count}>
                <dt>{label}</dt>
                <dd>{count.toLocaleString()}</dd>
              </div>
            ))}
          </dl>

          <ul className={styles.items} aria-label={strings.clients.convertListLabel}>
            {data.items.map((item) => (
              <Item key={item.id} item={item} />
            ))}
          </ul>
        </>
      )}
    </BottomSheet>
  );
}
